<template>
  <div class="role-grid-wrapper">
    <div class="roles-grid">
      <div
        v-for="role in sortedRoles"
        :key="role.id"
        class="role-card cursor-pointer"
        :class="{
          'selected': selectedRoles[role.id],
          'disabled': isRoleDisabled(role)
        }"
        @click="handleRoleClick(role)"
      >
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div class="role-card__check">
            <CIcon name="cipCheckbox" size="custom-size" :width="20" :class="['checkbox-icon', { 'selected': selectedRoles[role.id] }]"/>
          </div>
          <div class="role-name font-weight-medium mr-1">
            {{ role.display_name || role.name }}
            <RoleLimitBadge
              v-if="showLimits && getRoleLimitUsage(role)"
              :usage="getRoleLimitUsage(role)"
              class="ml-2"
            />
            <small class="d-block" v-if="showRealName(role)"> ({{ role.name }})</small>
          </div>
          <div class="d-flex align-items-center" style="gap: 4px;">
            <span
              v-if="showInfoIcon"
              class="role-info-icon-wrapper"
              @click.stop="showPermissions(role)"
            >
              <CIcon
                name="cipInfo"
                size="custom-size"
                :hight="18"
                :width="18"
                class="role-info-icon text-primary"
              />
            </span>
            <CBadge
              v-if="role.role_type"
              :color="getRoleTypeBadgeColor(role.role_type)"
              class="role-type-badge"
            >
              {{ getRoleTypeLabel(role.role_type) }}
            </CBadge>
          </div>
        </div>
        <div v-if="role.description" class="role-description" v-html="role.description"></div>
      </div>

      <div v-if="roles.length === 0" class="text-muted">
        <translate translate-context="collaborators.roles">No roles available</translate>
      </div>
    </div>
  </div>
</template>

<script>
import {sortRolesByPriority} from '../utils/roles'
import RoleLimitBadge from './RoleLimitBadge.vue'

export default {
  name: 'RoleGrid',

  components: {
    RoleLimitBadge
  },

  props: {
    roles: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      // v-model: oggetto {roleId: boolean}
      type: Object,
      required: true,
      default: () => ({})
    },
    roleLimits: {
      // {roleCode: {limit, current, available}}
      type: Object,
      default: null
    },
    showLimits: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    sortedRoles() {
      return sortRolesByPriority(this.roles)
    },

    selectedRoles() {
      return this.value
    },

    isAdvancedUser() {
      return this.hasPerm('collaborator.advanced')
    },

    showInfoIcon() {
      return this.isAdvancedUser
    }
  },

  methods: {
    showRealName(role) {
      return this.isAdvancedUser && role.display_name && role.display_name !== role.name
    },

    toggleRole(roleId) {
      const newValue = {...this.value}
      newValue[roleId] = !newValue[roleId]
      this.$emit('input', newValue)
    },

    handleRoleClick(role) {
      if (this.isRoleDisabled(role)) {
        return
      }
      this.toggleRole(role.id)
    },

    isRoleDisabled(role) {
      if (!this.showLimits || !this.roleLimits) {
        return false
      }
      const usage = this.getRoleLimitUsage(role)
      if (!usage) {
        return false
      }
      // Disable only if available <= 0 AND role is not already selected
      return usage.available <= 0 && !this.selectedRoles[role.id]
    },

    getRoleLimitUsage(role) {
      if (!this.roleLimits || !role.code) {
        return null
      }
      return this.roleLimits[role.code] || null
    },

    showPermissions(role) {
      this.$emit('show-permissions', role)
    },

    getRoleTypeLabel(roleType) {
      const labels = {
        'global': this.$pgettext('collaborators.roles', 'Global'),
        'contract_manager': this.$pgettext('collaborators.roles', 'Contract Mgr'),
        'pica_service': this.$pgettext('collaborators.roles', 'Service')
      }
      return labels[roleType] || roleType
    },

    getRoleTypeBadgeColor(roleType) {
      const colors = {
        'global': 'danger',
        'contract_manager': 'warning',
        'pica_service': 'info'
      }
      return colors[roleType] || 'secondary'
    }
  }
}
</script>

<style scoped lang="scss">
.role-grid-wrapper {
  width: 100%;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  overflow-y: auto;
  padding: 2px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.role-card {
  border: 2px solid #d8dbe0;
  border-radius: 8px;
  padding: 12px;
  background-color: #fff;
}

.role-card.selected {
  border-color: #321fdb;
  background-color: #e7f1ff;
}

.role-card.disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #f5f5f5;
}

.role-card.disabled:hover {
  transform: none;
  box-shadow: none;
  border-color: #d8dbe0;
}

.role-name {
  flex: 1;
}

.role-description {
  font-size: 12px;
  color: #768192;
  margin-top: 4px;
  line-height: 1.4;
}

.role-description ::v-deep p {
  margin: 0;
}

.role-type-badge {
  font-size: 10px;
  padding: 2px 6px;
  flex-shrink: 0;
  text-transform: uppercase;
  font-weight: 600;
}

.role-info-icon-wrapper {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}



.role-info-icon-wrapper:hover .role-info-icon {
  color: #321fdb;
}

</style>
